import type {
  ReportGeneratorTemplateModuleSchema,
  ReportGeneratorTemplateSchema,
} from "@api/generated";

import { toTitleCase } from "@utils/string";

import {
  CODE_OF_CONDUCT_HEADING_TITLE,
  isAutoInsertedModule,
  normaliseModuleName,
} from "../constants";

/** Per-module content as the split-pane page sees it. */
export type ModuleContent = {
  name: string;
  label: string;
  /** Plain-text mirror — what the left textarea binds to. */
  text: string;
  /** Rich HTML — what the right-pane slot renders. */
  html: string;
};

/** Order of (sectionName, module) tuples to walk through the template. */
export type OrderedModule = {
  sectionName: string;
  module: ReportGeneratorTemplateModuleSchema;
};

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * Wrap a plain-text string into one or more `<p>` blocks. Empty paragraphs are
 * preserved so a textarea with one blank line still produces a visible block
 * in the editor (otherwise typing in the textarea looks like nothing happens
 * until the first non-blank line).
 */
export const plainTextToHtml = (text: string): string => {
  const trimmed = text ?? "";
  if (!trimmed.trim()) return "<p></p>";
  return trimmed
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`)
    .join("");
};

/**
 * Strip HTML back to plain text for the textarea mirror. Block-level elements
 * become double-newline separated paragraphs; `<br>` becomes a single newline.
 */
const BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "LI",
  "BR",
]);

export const stripHtmlToText = (html: string): string => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  const out: string[] = [];

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out.push(node.textContent ?? "");
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    for (const child of Array.from(node.childNodes)) {
      walk(child);
    }
    if (BLOCK_TAGS.has((node as Element).tagName)) {
      out.push("\n");
    }
  };

  walk(doc.body);
  return out
    .join("")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

/** One choice entry on a module's first field (Code-of-Conduct-style). */
export type ModuleChoice = {
  key: string;
  display_name: string;
  description: string;
};

/**
 * Return the ordered list of choices for a module if its first field has
 * `option_choices.choices` (i.e. it's a radio/boolean module like Code of
 * Conduct, Conflict of Interest, Unusual Circumstances). Returns `null` for
 * freeform-text modules.
 */
export const getModuleChoices = (
  module: ReportGeneratorTemplateModuleSchema
): ModuleChoice[] | null => {
  const choices = module.fields?.[0]?.option_choices?.choices as
    | Record<string, { display_name?: string; description?: string }>
    | undefined;
  if (!choices) return null;
  const entries = Object.entries(choices);
  if (entries.length === 0) return null;
  return entries.map(([key, value]) => ({
    key,
    display_name: value?.display_name?.trim() || key,
    description: value?.description?.trim() || "",
  }));
};

/**
 * Match a saved `draft.value` against a module's choices (case-insensitive)
 * and return the canonical choice key. Falls back to the first choice when
 * nothing matches.
 */
export const resolveChoiceKey = (
  choices: ModuleChoice[],
  draftValue: string | null | undefined
): string => {
  if (draftValue?.trim()) {
    const matched = choices.find(
      (c) => c.key.toLowerCase() === draftValue.toLowerCase()
    );
    if (matched) return matched.key;
  }
  return choices[0].key;
};

/** Flatten a template into an ordered list of (section, module) tuples. */
/** Canonical name for the synthetic Code of Conduct module (MDRS-139). */
export const CODE_OF_CONDUCT_MODULE_NAME = "Code of Conduct";

const NORMALISED_COC_VARIANTS = CODE_OF_CONDUCT_HEADING_TITLE.map(
  normaliseModuleName
).filter((v) => v.length > 0);

/**
 * True when a section key/module name is the Code of Conduct section (MDRS-139).
 * Templates ship CoC under any of the `CODE_OF_CONDUCT_HEADING_TITLE` variants
 * (e.g. "CODE OF CONDUCT" uppercase), not just the canonical synthetic name.
 *
 * Normalises the name the same way as `isAutoInsertedModule` (lowercase, strip
 * leading numbering/punctuation, collapse whitespace) so a heading like
 * "1. CODE OF CONDUCT" or "code of conduct" that already reads as authorable
 * also activates the CoC panel — otherwise it falls back to Notes & Snippets,
 * the "reverted UI" bug this was meant to fix.
 *
 * Match is exact-or-heading-contains-variant only (not the reverse): a section
 * merely named "Code" or "Conduct" must NOT be mistaken for CoC.
 */
export const isCodeOfConductSection = (name: string | null): boolean => {
  if (name == null) return false;
  const n = normaliseModuleName(name);
  if (!n) return false;
  return NORMALISED_COC_VARIANTS.some((v) => n === v || n.includes(v));
};

/**
 * Build a synthetic, freeform Code of Conduct module. Templates don't always
 * declare CoC as a module (the service-type seeds omit it entirely), but
 * MDRS-139 makes CoC an authorable section in the modules panel. We inject a
 * heading-only stub so the section row renders and selecting it opens the
 * browse-and-copy panel. No CoC heading is synthesised into the docx — MDRS-140
 * dropped CoC auto-insertion, so the expert copies a declaration from the panel
 * and pastes it into the draft themselves.
 *
 * `id` is fabricated and never read — the ReportBuilder keys everything on
 * `name` (seed, moduleContent, save payload, docx heading matching).
 */
const syntheticCodeOfConductModule = (): ReportGeneratorTemplateModuleSchema => ({
  id: "synthetic-code-of-conduct",
  name: CODE_OF_CONDUCT_MODULE_NAME,
  order: 0,
  fields: [],
  created_at: "",
  updated_at: "",
});

export const orderedModulesFromTemplate = (
  template: ReportGeneratorTemplateSchema | undefined
): OrderedModule[] => {
  if (!template) return [];
  const out: OrderedModule[] = [];
  const sections = (template.sections ?? [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const section of sections) {
    const modules = (section.modules ?? [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    for (const module of modules) {
      out.push({ sectionName: section.name, module });
    }
  }

  // MDRS-139: guarantee a Code of Conduct section even when the template
  // doesn't declare one. Prepend the synthetic stub so CoC sorts first (its
  // canonical position), unless the template already ships a CoC module.
  const hasCoc = out.some(({ module }) =>
    CODE_OF_CONDUCT_HEADING_TITLE.includes(module.name)
  );
  if (!hasCoc) {
    out.unshift({
      sectionName: CODE_OF_CONDUCT_MODULE_NAME,
      module: syntheticCodeOfConductModule(),
    });
  }

  return out;
};

/** Saved-draft shape for a single module (subset we actually read). */
export type ModuleDraft = {
  value?: string | null;
  experts_response?: string | null;
  experts_response_html?: string | null;
  generated_collective_response?: string | null;
  summaries_from_doc_sorter?: string | null;
  summaries_from_assessment_scribe?: string | null;
};

/**
 * Resolve the seed HTML for a module — preferred over `resolveSeedText` when
 * we want to round-trip rich-text artefacts (like `<span data-refined-original>`
 * markers that drive Original Wording) through save/reload.
 *
 * Falls back to converting the plain-text seed when no html is stored yet.
 */
export const resolveSeedHtml = (
  module: ReportGeneratorTemplateModuleSchema,
  draft: ModuleDraft | undefined
): string => {
  if (draft?.experts_response_html?.trim()) return draft.experts_response_html;
  const text = resolveSeedText(module, draft);
  return plainTextToHtml(text);
};

/** Resolve the seed text for a module from its saved draft data. */
export const resolveSeedText = (
  module: ReportGeneratorTemplateModuleSchema,
  draft: ModuleDraft | undefined
): string => {
  if (draft?.experts_response?.trim()) return draft.experts_response;
  if (draft?.generated_collective_response?.trim())
    return draft.generated_collective_response;

  const choices = module.fields?.[0]?.option_choices?.choices as
    | Record<string, { description?: string }>
    | undefined;

  if (draft?.value?.trim()) {
    if (choices) {
      const matched = Object.entries(choices).find(
        ([key]) => key.toLowerCase() === draft.value!.toLowerCase()
      );
      if (matched?.[1]?.description) return matched[1].description;
    }
    return draft.value;
  }

  // Radio module with no saved selection — fall back to the first option's
  // description so the editor still shows something meaningful. The user can
  // edit the text directly; saving treats it as freeform expert response.
  if (choices) {
    const first = Object.values(choices)[0];
    if (first?.description?.trim()) return first.description;
  }

  return "";
};

/**
 * Build the initial Tiptap-ready HTML for the right pane.
 *
 * Strategy: take the rendered template chrome (already substituted with case
 * placeholders), find the "Thank you for…" paragraph, and insert one
 * `<div data-module="…">` per module between it and the "Yours sincerely"
 * paragraph. Each slot's inner content is the module's seed text wrapped
 * as paragraphs.
 *
 * If we can't find the anchors (older or atypical templates), fall back to
 * appending all slots at the end of the chrome so nothing is lost.
 */
export const composeInitialEditorHtml = (
  chromeHtml: string,
  modules: ModuleContent[]
): string => {
  const doc = new DOMParser().parseFromString(
    chromeHtml || "<div></div>",
    "text/html"
  );

  // Build the slot block on a detached fragment.
  //
  // Each module's `html` field is mounted as-is — it may already contain
  // marks (e.g. `<span data-refined-original>`) that we want to preserve
  // across save/reload. If `html` is empty, fall back to wrapping the
  // module's plain text.
  const fragment = doc.createDocumentFragment();
  for (const m of modules) {
    const wrapper = doc.createElement("div");
    wrapper.setAttribute("data-module", m.name);
    wrapper.setAttribute("data-module-label", m.label);
    const heading = doc.createElement("h2");
    heading.textContent = m.label;
    wrapper.appendChild(heading);
    const tmpl = doc.createElement("template");
    tmpl.innerHTML = m.html?.trim() ? m.html : plainTextToHtml(m.text);
    wrapper.appendChild(tmpl.content);
    fragment.appendChild(wrapper);
  }

  const paragraphs = Array.from(doc.querySelectorAll("p"));
  const thankYouIdx = paragraphs.findIndex((p) =>
    /Thank you for|I write regarding/i.test(p.textContent ?? "")
  );
  const signatureIdx = paragraphs.findIndex((p) =>
    /Yours (sincerely|faithfully)/i.test(p.textContent ?? "")
  );

  if (thankYouIdx >= 0 && signatureIdx > thankYouIdx) {
    // Remove anything between the two anchors (Tiptap render of the template
    // may have left placeholder body text we don't want).
    for (let i = signatureIdx - 1; i > thankYouIdx; i--) {
      paragraphs[i].remove();
    }
    paragraphs[thankYouIdx].after(fragment);
  } else if (thankYouIdx >= 0) {
    paragraphs[thankYouIdx].after(fragment);
  } else {
    doc.body.appendChild(fragment);
  }

  return doc.body.innerHTML;
};

export type SectionStatus = "applied" | "draft" | "missing";

export type SectionRow = {
  key: string;
  number: number;
  label: string;
  status: SectionStatus;
  /** Count of mapped notes (extracts) for this section — col-1 badge. */
  noteCount: number;
  /** True for the browse-and-copy Code of Conduct section (MDRS-139). */
  isCodeOfConduct: boolean;
};

type BuildSectionRowsInput = {
  orderedModules: OrderedModule[];
  moduleContent: Record<string, ModuleContent>;
  /** Module names already persisted to the backend (drives "applied"). */
  acceptedModuleNames: Set<string>;
  /** Mapped-note count per module name — drives the col-1 badge. */
  noteCountBySection: Record<string, number>;
};

/**
 * Build the col-1 section list.
 *
 * The four preamble sections (Code of Conduct, Conflict of Interest, Other
 * Attendees, Unusual Circumstances) and the LOI-derived Question To Answer
 * block are auto-inserted straight into the docx editor — the user can't
 * author them through the modules panel — so they're always excluded.
 *
 * Every remaining (authorable) section is shown, regardless of whether it has
 * mapped notes. The note count still drives the col-1 badge but no longer
 * gates visibility.
 *
 * Visible rows are numbered 1..N so the ordinals stay contiguous.
 */
export const buildSectionRows = ({
  orderedModules,
  moduleContent,
  acceptedModuleNames,
  noteCountBySection,
}: BuildSectionRowsInput): SectionRow[] => {
  const visible = orderedModules.filter(
    ({ module }) => !isAutoInsertedModule(module.name)
  );

  return visible.map(({ module }, i) => {
    const text = moduleContent[module.name]?.text?.trim() ?? "";
    let status: SectionStatus;
    if (!text) status = "missing";
    else if (acceptedModuleNames.has(module.name)) status = "applied";
    else status = "draft";
    return {
      key: module.name,
      number: i + 1,
      label: toTitleCase(module.name),
      status,
      noteCount: noteCountBySection[module.name] ?? 0,
      isCodeOfConduct: isCodeOfConductSection(module.name),
    };
  });
};
